import NavigationControl from '@/components/navigationControl/NavigationControl';
import CubeControl from '@/components/cubeControl/CubeControl';
import AnatomyOrientationControl from '@/components/cubeControl/anatomyOrientationControl/AnatomyOrientationControl';

import styled from 'styled-components';

import { renderMaximizeIconContrast } from './renderers';
import { useViewPortManipulationApi } from '../useViewPortManipulationApi';
import { useTheme } from 'styled-components';
import { ViewPort } from '@/components/viewPort';
import { Menu, MenuItem } from '@mui/material';
import { FluoroAnglesLabels } from '@/components/fluoroAngles/FluoroAnglesLabels';
import { CubeContainer } from './containers/CubeContainer';

import { useSurgerySpecificUiConfigValue } from '@/engine/app/modules/ui';
import { useContextMenu } from './useContextMenu';

import type { ComponentType, FunctionComponent, MouseEventHandler, SVGProps } from 'react';
import type { PropsWithChildren, FC } from 'react';
import type { IThreeDViewProps } from './types';
import type { ViewOrientationKind } from '@/engine/app/interfaces';
import type { ViewHelper } from '@/engine/app/models/viewEntity/view.entity.ts';
import type { AnatomyOrientationExtensionComponent } from '@/components/cubeControl/anatomyOrientationControl/interfaces';
import type { IOrientationIntegrationApi } from '@/engine/app/modules/ui/UIConfigSchema';

import { ContextEvents } from '@/engine/app/modules/images/interfaces.ts';
import { FluoroAnglesPlacement } from '@/components/fluoroAngles/interfaces.ts';
import { ResetCameraStrategy } from '@/components/navigationControl/NavigationController';
import { useOptionalSurgerySpecificUiConfigValue } from '@/engine/app/modules/ui/useUiConfigValue';
import { useDeviceOrientationChange } from './useDeviceOrientationChange';

interface Props {
  isMaximized: boolean;
  threeDView: IThreeDViewProps;
  viewOrientationDescriptor: ViewOrientationKind;
  helpers: Array<ViewHelper>;
  isCollapsed: boolean;
  onMaximize: () => void;
  onMinimize?: () => void;
}

export const ThreeDViewPortWrapper: FC<PropsWithChildren<Props>> = ({
  isMaximized,
  threeDView,
  children,
  viewOrientationDescriptor,
  onMaximize,
  onMinimize
}) => {
  const theme = useTheme();

  const anatomyIcon = useSurgerySpecificUiConfigValue<
    (viewPortDescriptor: string) => FunctionComponent<SVGProps<SVGSVGElement>>
  >('anatomyOrientationControl.getAnatomyIcon')(viewOrientationDescriptor);

  const anatomyOrientationExtensionComponent =
    useOptionalSurgerySpecificUiConfigValue<AnatomyOrientationExtensionComponent>(
      'anatomyOrientationControl.extensionComponent'
    );

  const SurgerySpecificThreeDOverlay = useSurgerySpecificUiConfigValue<
    ComponentType<{ integrationApi: IOrientationIntegrationApi }>
  >('threeDViewOverlay', () => null);

  const showFluoroAnglesLabels = useSurgerySpecificUiConfigValue<boolean>('showFluoroAnglesLabels');

  const [
    viewPortManipulationApi,
    navigationControlIntegrationApi,
    viewPortContextIntegrationApi,
    fluoroAnglesIntegrationApi,
    cubeControlIntegrationApi
  ] = useViewPortManipulationApi();

  const [contextMenu, contextMode, setContextMode, handleContextMenu, handleContextClose, handleRemove, handleAdd] =
    useContextMenu(viewPortContextIntegrationApi);

  useDeviceOrientationChange();

  return (
    <Container
      $isMaximized={isMaximized}
      onContextMenu={handleContextMenu as unknown as MouseEventHandler<HTMLDivElement>}
      data-testid="threeDViewContainer"
      data-guide-id="threeDViewContainer"
    >
      {/* {isMaximized && renderColorLegend(helpers)} */}
      <NavigationControl
        data-testid="3dNavigationControl"
        descriptor={threeDView.descriptor}
        isMaximized={isMaximized}
        integrationApi={navigationControlIntegrationApi}
        resetCameraStrategy={ResetCameraStrategy.CentrifyAndDefaultOrientation}
      />
      <ViewPort
        style={{ height: '100%' }}
        bgColor={theme.customPalette.panels.threeD}
        descriptor={threeDView.descriptor}
        viewPortManipulationApi={viewPortManipulationApi}
        isMaximized={isMaximized}
        contextStateApi={{ setContextState: setContextMode }}
      />
      {showFluoroAnglesLabels && (
        <FluoroAnglesLabels
          theme={theme}
          $placement={FluoroAnglesPlacement.ThreeD}
          $isMaximized={isMaximized}
          $integrationApi={fluoroAnglesIntegrationApi}
        />
      )}
      {!isMaximized && (
        <MaximizeIcon3DWrapper onClick={onMaximize} className="max-icon">
          {renderMaximizeIconContrast(theme.mode)}
        </MaximizeIcon3DWrapper>
      )}
      {isMaximized && onMinimize && (
        <MaximizeIcon3DWrapper onClick={onMinimize} className="min-icon">
          {renderMaximizeIconContrast(theme.mode)}
        </MaximizeIcon3DWrapper>
      )}

      {false && (
        <CubeContainer>
          <div>
            <CubeControl bgColor={theme.customPalette.panels.threeD} integrationApi={cubeControlIntegrationApi} />
          </div>
        </CubeContainer>
      )}
      {isMaximized && (
        <AnatomyOrientationContainer>
          <AnatomyOrientationControl
            data-testid="anatomyOrientationControl"
            integrationApi={cubeControlIntegrationApi}
            extensionComponent={anatomyOrientationExtensionComponent ?? undefined}
            AnatomyIcon={anatomyIcon}
          />
        </AnatomyOrientationContainer>
      )}

      <Menu
        open={contextMenu !== null}
        onClose={handleContextClose}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        {contextMode === ContextEvents.ADD && <MenuItem onClick={handleAdd}>Add point</MenuItem>}
        {contextMode === ContextEvents.REMOVE && <MenuItem onClick={handleRemove}>Delete point</MenuItem>}
      </Menu>

      {isMaximized && <SurgerySpecificThreeDOverlay integrationApi={cubeControlIntegrationApi} />}

      {children}
    </Container>
  );
};

export const Container = styled.div<{ $isMaximized: boolean }>`
  display: flex;
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const MaximizeIcon3DWrapper = styled.button`
  position: absolute;
  margin-left: 2px;
  margin-top: 2px;
  background: none;
  border: 0;
`;

const AnatomyOrientationContainer = styled.div``;
